import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Colors } from '@stamhoofd/utility';
import { Address } from './addresses/Address.js';
import { STPackageStatus, STPackageType } from './billing/STPackage.js';
import { Company } from './Company.js';
import { Replacement } from './endpoints/EmailRequest.js';
import { File } from './files/File.js';
import { Image } from './files/Image.js';
import { GroupCategory } from './GroupCategory.js';
import { OrganizationRecordsConfiguration } from './members/OrganizationRecordsConfiguration.js';
import { OldGroupPrices } from './OldGroupPrices.js';
import { OrganizationGenderType } from './OrganizationGenderType.js';
import { OrganizationType } from './OrganizationType.js';
import { PaymentConfiguration } from './PaymentConfiguration.js';
import { PaymentMethod } from './PaymentMethod.js';
import { UmbrellaOrganization } from './UmbrellaOrganization.js';
import { TransferSettings } from './webshops/TransferSettings.js';
import { UitpasClientCredentialsStatus } from './UitpasClientCredentialsStatus.js';
export class OrganizationPackages extends AutoEncoder {
    packages = new Map();
    isActive(type) {
        if (STAMHOOFD.userMode === 'platform') {
            if (type === STPackageType.Webshops || type === STPackageType.Members) {
                return true;
            }
            return false;
        }
        const status = this.packages.get(type);
        if (!status) {
            return false;
        }
        if (!status.isActive) {
            return false;
        }
        return true;
    }
    /**
     * Return amount of ms this package has been active for
     */
    getActiveTime(type) {
        const status = this.packages.get(type);
        if (!status) {
            return null;
        }
        if (!status.isActive) {
            return null;
        }
        return Math.max(0, Date.now() - status.startDate.getTime());
    }
    wasActive(type) {
        const status = this.packages.get(type);
        if (!status) {
            return false;
        }
        if (!status.wasActive) {
            return false;
        }
        return true;
    }
    /**
     * Return amount of ms this package has been active for
     */
    getDeactivatedTime(type) {
        const status = this.packages.get(type);
        if (!status) {
            return null;
        }
        if (!status.wasActive) {
            return null;
        }
        const deactivateDate = status.deactivateDate;
        if (deactivateDate === null) {
            return null;
        }
        return Math.max(0, Date.now() - deactivateDate.getTime());
    }
    get useMembers() {
        return this.isActive(STPackageType.Members) || this.isActive(STPackageType.LegacyMembers) || this.isActive(STPackageType.TrialMembers);
    }
    set useMembers(_) {
        console.warn('Deprected set on useMembers');
    }
    get canStartMembersTrial() {
        return !this.useMembers && !this.wasActive(STPackageType.Members);
    }
    get isMembersTrial() {
        return !this.isActive(STPackageType.Members) && !this.isActive(STPackageType.LegacyMembers) && this.isActive(STPackageType.TrialMembers);
    }
    get isActivitiesTrial() {
        return !this.isActive(STPackageType.Members) && this.isActive(STPackageType.LegacyMembers) && this.isActive(STPackageType.TrialMembers);
    }
    get isWebshopsTrial() {
        return !this.isActive(STPackageType.Webshops) && !this.isActive(STPackageType.SingleWebshop) && this.isActive(STPackageType.TrialWebshops);
    }
    get canStartWebshopsTrial() {
        return !this.useWebshops && !this.wasActive(STPackageType.Webshops) && !this.wasActive(STPackageType.SingleWebshop);
    }
    get useWebshops() {
        return this.webshopLimit > 0;
    }
    set useWebshops(_) {
        console.warn('Deprected set on useWebshops');
    }
    get webshopLimit() {
        if (this.isActive(STPackageType.Webshops)) {
            return 10;
        }
        if (this.isActive(STPackageType.SingleWebshop)) {
            return 1;
        }
        if (this.isActive(STPackageType.TrialWebshops)) {
            return 10;
        }
        return 0;
    }
    get disableActivities() {
        return !this.useActivities;
    }
    get useActivities() {
        return this.isActive(STPackageType.Members) || this.isActive(STPackageType.TrialMembers);
    }
    get isPaid() {
        return this.isActive(STPackageType.Members) || this.isActive(STPackageType.LegacyMembers) || this.isActive(STPackageType.Webshops) || this.isActive(STPackageType.SingleWebshop);
    }
    get wasPaid() {
        return this.wasActive(STPackageType.Members) || this.wasActive(STPackageType.LegacyMembers) || this.wasActive(STPackageType.Webshops) || this.wasActive(STPackageType.SingleWebshop);
    }
}
__decorate([
    field({ decoder: new MapDecoder(new EnumDecoder(STPackageType), STPackageStatus) })
], OrganizationPackages.prototype, "packages", void 0);
/**
 * @deprecated
 */
export class OrganizationModules extends AutoEncoder {
    useMembers = false;
    useWebshops = false;
    /**
     * We use inverse property here because this can only be used in combination with useMembers == true
     */
    disableActivities = false;
    get useActivities() {
        return this.useMembers && !this.disableActivities;
    }
}
__decorate([
    field({ decoder: BooleanDecoder })
], OrganizationModules.prototype, "useMembers", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], OrganizationModules.prototype, "useWebshops", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 63 })
], OrganizationModules.prototype, "disableActivities", void 0);
export class OrganizationMetaData extends AutoEncoder {
    /**
     * Last time the organization signed the terms. Null means the creation date of the organization.
     */
    lastSignedTerms = null;
    get didAcceptLatestTerms() {
        return this.lastSignedTerms !== null && this.lastSignedTerms >= new Date(2022, 0, 20, 0, 0, 0, 0);
    }
    get didAcceptEndToEndEncryptionRemoval() {
        return this.lastSignedTerms !== null && this.lastSignedTerms >= new Date(2022, 0, 20, 0, 0, 0, 0);
    }
    type = OrganizationType.Other;
    /**
     * Contains the ids of the tags
     */
    tags = [];
    /**
     * Show beta features in this organization
     */
    enableBetaFeatures = false;
    /**
     * @deprecated Only used for migrations
     */
    modulesOld = OrganizationModules.create({});
    /**
     * @deprecated
     * Use packages
     */
    get modules() {
        return this.packages;
    }
    set modules(_) {
        console.error('Deprecated set on modules');
    }
    packages = OrganizationPackages.create({});
    umbrellaOrganization = null;
    /**
     * Set either file or url for the privacy policy. If both are set, the url has priority
     */
    privacyPolicyFile = null;
    privacyPolicyUrl = null;
    /**
     * Logo used in a horizontal environment (e.g. menu bar)
     */
    horizontalLogo = null;
    /**
     * Logo to display (small)
     */
    squareLogo = null;
    expandLogo = false;
    horizontalLogoDark = null;
    squareLogoDark = null;
    color = null;
    // Everything below should move to registrations meta data
    /**
     * Only used for building the initial groups for some type of organizations
     */
    genderType = OrganizationGenderType.Mixed;
    /**
     * @deprecated
     */
    defaultStartDate = new Date();
    /**
     * @deprecated
     */
    defaultEndDate = new Date();
    /**
     * @deprecated
     */
    defaultPrices = [];
    /**
     * @deprecated
     * Use registrationPaymentConfiguration.transferSettings instead
     */
    oldTransferSettings = TransferSettings.create({});
    /**
     * @deprecated
     * Use registrationPaymentConfiguration.paymentMethods instead
     */
    oldPaymentMethods = [PaymentMethod.Transfer];
    registrationPaymentConfiguration = PaymentConfiguration.create({ paymentMethods: [PaymentMethod.PointOfSale] });
    /**
     * @deprecated
     * Use registrationPaymentConfiguration.paymentMethods instead
     */
    get paymentMethods() {
        return this.registrationPaymentConfiguration.paymentMethods;
    }
    /**
     * @deprecated
     * Use registrationPaymentConfiguration.paymentMethods instead
     */
    get transferSettings() {
        return this.registrationPaymentConfiguration.transferSettings;
    }
    recordsConfiguration;
    /**
     * @deprecated Moved to companies
     */
    companyName = null;
    /**
     * @deprecated Moved to companies
     */
    VATNumber = null;
    /**
     * @deprecated Moved to companies
     */
    companyNumber = null;
    /**
     * @deprecated Moved to companies
     */
    companyAddress = null;
    companies = [];
    /**
     * @deprecated
     * Use OrganizationRegistrationPeriod instead
     *
     * All the available categories
     */
    categories = [GroupCategory.create({ id: 'root' })]; // we use ID root here because this ID needs to stay the same since it won't be saved
    /**
     * @deprecated
     * Use OrganizationRegistrationPeriod instead
     *
     * We use one invisible root category to simplify the difference between non-root and root category
     */
    rootCategoryId = this.categories[0]?.id ?? '';
    uitpasOrganizerId = null;
    uitpasOrganizerName = null;
    uitpasClientCredentialsStatus = UitpasClientCredentialsStatus.NotConfigured;
    /**
     * @deprecated
     * Use OrganizationRegistrationPeriod instead
     *
     * (todo) Contains the fully build hierarchy without the need for ID lookups. Try not to use this tree when modifying it.
     */
    get rootCategory() {
        return this.categories.find(c => c.id === this.rootCategoryId);
    }
    getEmailReplacements(organization) {
        const base = [
            Replacement.create({
                token: 'organizationName',
                value: organization.name,
            }),
        ];
        const fromAddress = organization.privateMeta?.emails?.find(e => e.default) || organization.privateMeta?.emails[0];
        if (fromAddress) {
            base.push(Replacement.create({
                token: 'fromAddress',
                value: fromAddress.email,
            }));
            base.push(Replacement.create({
                token: 'fromName',
                value: fromAddress.name ?? organization.name,
            }));
        }
        else {
            base.push(Replacement.create({
                token: 'fromName',
                value: organization.name,
            }));
        }
        if (this.color) {
            base.push(Replacement.create({
                token: 'primaryColor',
                value: this.color ? this.color : '#0053ff',
            }), Replacement.create({
                token: 'primaryColorContrast',
                value: this.color ? Colors.getContrastColor(this.color) : '#fff',
            }));
        }
        base.push(Replacement.create({
            token: 'registerUrl',
            value: organization.registerUrl,
        }));
        return base;
    }
    /**
     * True if at least one id matches
     */
    matchTags(ids) {
        for (const id of ids) {
            if (this.tags.includes(id)) {
                return true;
            }
        }
        return false;
    }
}
__decorate([
    field({ decoder: DateDecoder, nullable: true, version: 147 })
], OrganizationMetaData.prototype, "lastSignedTerms", void 0);
__decorate([
    field({ decoder: new EnumDecoder(OrganizationType) })
], OrganizationMetaData.prototype, "type", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), version: 260 })
], OrganizationMetaData.prototype, "tags", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 108 })
], OrganizationMetaData.prototype, "enableBetaFeatures", void 0);
__decorate([
    field({ decoder: OrganizationModules, version: 48, upgrade: () => OrganizationModules.create({ useMembers: true, useWebshops: true }), field: 'modules' })
], OrganizationMetaData.prototype, "modulesOld", void 0);
__decorate([
    field({ decoder: OrganizationPackages, version: 87 })
], OrganizationMetaData.prototype, "packages", void 0);
__decorate([
    field({ decoder: new EnumDecoder(UmbrellaOrganization), nullable: true })
], OrganizationMetaData.prototype, "umbrellaOrganization", void 0);
__decorate([
    field({ decoder: File, nullable: true, version: 25 })
], OrganizationMetaData.prototype, "privacyPolicyFile", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 25 })
], OrganizationMetaData.prototype, "privacyPolicyUrl", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 11 })
], OrganizationMetaData.prototype, "horizontalLogo", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 11 })
], OrganizationMetaData.prototype, "squareLogo", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], OrganizationMetaData.prototype, "expandLogo", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 185 })
], OrganizationMetaData.prototype, "horizontalLogoDark", void 0);
__decorate([
    field({ decoder: Image, nullable: true, version: 185 })
], OrganizationMetaData.prototype, "squareLogoDark", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 21 })
], OrganizationMetaData.prototype, "color", void 0);
__decorate([
    field({ decoder: new EnumDecoder(OrganizationGenderType), optional: true })
], OrganizationMetaData.prototype, "genderType", void 0);
__decorate([
    field({ decoder: DateDecoder, optional: true })
], OrganizationMetaData.prototype, "defaultStartDate", void 0);
__decorate([
    field({ decoder: DateDecoder, optional: true })
], OrganizationMetaData.prototype, "defaultEndDate", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(OldGroupPrices), optional: true })
], OrganizationMetaData.prototype, "defaultPrices", void 0);
__decorate([
    field({
        decoder: TransferSettings,
        version: 50,
        field: 'transferSettings',
        optional: true, // We no longer expect this from the backend, so it can get removed in a future version
    }),
    field({
        decoder: TransferSettings,
        version: 208,
        field: 'oldTransferSettings',
        optional: true, // We no longer expect this from the backend, so it can get removed in a future version
        downgrade: function () {
            return this.transferSettings;
        },
    })
], OrganizationMetaData.prototype, "oldTransferSettings", void 0);
__decorate([
    field({
        decoder: new ArrayDecoder(new EnumDecoder(PaymentMethod)),
        version: 151,
        field: 'paymentMethods',
        optional: true,
    }),
    field({
        decoder: new ArrayDecoder(new EnumDecoder(PaymentMethod)),
        version: 208,
        field: 'oldPaymentMethods',
        optional: true, // We no longer expect this from the backend, so it can get removed in a future version
        downgrade: function () {
            // This return value for old clients
            return this.paymentMethods;
        },
    })
], OrganizationMetaData.prototype, "oldPaymentMethods", void 0);
__decorate([
    field({
        decoder: PaymentConfiguration,
        version: 204,
        upgrade: function () {
            return PaymentConfiguration.create({
                transferSettings: this.oldTransferSettings,
                paymentMethods: this.oldPaymentMethods,
            });
        },
    })
], OrganizationMetaData.prototype, "registrationPaymentConfiguration", void 0);
__decorate([
    field({
        decoder: OrganizationRecordsConfiguration,
        version: 53,
        defaultValue: () => OrganizationRecordsConfiguration.create({}),
    })
], OrganizationMetaData.prototype, "recordsConfiguration", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 113, field: 'companyName', optional: true })
], OrganizationMetaData.prototype, "companyName", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 113, field: 'VATNumber', optional: true })
], OrganizationMetaData.prototype, "VATNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 113, field: 'companyNumber', optional: true })
], OrganizationMetaData.prototype, "companyNumber", void 0);
__decorate([
    field({ decoder: Address, nullable: true, version: 113, field: 'companyAddress', optional: true })
], OrganizationMetaData.prototype, "companyAddress", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Company), upgrade: function () {
            return (this.companyName || this.VATNumber || this.companyNumber || this.companyAddress)
                ? [
                    Company.create({
                        id: 'default', // required we have stable ids in upgrades
                        name: this.companyName ?? '',
                        VATNumber: this.VATNumber,
                        companyNumber: this.companyNumber,
                        address: this.companyAddress,
                    }),
                ]
                : [];
        }, version: 322 })
], OrganizationMetaData.prototype, "companies", void 0);
__decorate([
    field({
        decoder: new ArrayDecoder(GroupCategory),
        version: 57,
    })
], OrganizationMetaData.prototype, "categories", void 0);
__decorate([
    field({
        decoder: StringDecoder,
        version: 57,
    })
], OrganizationMetaData.prototype, "rootCategoryId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 378, nullable: true })
], OrganizationMetaData.prototype, "uitpasOrganizerId", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 378, nullable: true })
], OrganizationMetaData.prototype, "uitpasOrganizerName", void 0);
__decorate([
    field({ decoder: new EnumDecoder(UitpasClientCredentialsStatus), version: 378, defaultValue: () => UitpasClientCredentialsStatus.NotConfigured })
], OrganizationMetaData.prototype, "uitpasClientCredentialsStatus", void 0);
//# sourceMappingURL=OrganizationMetaData.js.map